'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useStore } from 'jotai'

import { useAuth } from '@/features/auth/hooks'
import { type PendingMakerQuote, quoteSlotKey } from '@/features/rfq/lib/rfq-maker-store'
import { markRfqCancelled } from '@/features/rfq/lib/rfq-store'
import {
  acceptRfqQuote,
  cancelRfq,
  cancelRfqMakerQuote,
  createRfq,
  fetchRfqAnalytics,
  getRfqHistory,
  getRfqMakers,
  sendRfqMakerQuote,
} from '@/service/rfq.api'
import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import { makerQuotesAtom, pendingMakerQuotesAtom, rfqsRawAtom } from '@/state/rfq'
import {
  AcceptRfqQuoteRequest,
  AcceptRfqQuoteResponse,
  CancelRfqMakerQuoteRequest,
  CancelRfqMakerQuoteResponse,
  CancelRfqRequest,
  CancelRfqResponse,
  CreateRfqRequest,
  CreateRfqResponse,
  RfqAnalytics,
  RfqAnalyticsRequest,
  SendRfqMakerQuoteRequest,
  SendRfqMakerQuoteResponse,
} from '@/types/generated/public_api'
import { RfqUpdate } from '@/types/generated/rfq'
import { ErrorResponse } from '@/types/service'

export const rfqKeys = {
  all: ['rfq'] as const,
  history: (userId: string, accountId: string) =>
    [...rfqKeys.all, 'history', userId, accountId] as const,
  makers: (userId: string, accountId: string) =>
    [...rfqKeys.all, 'makers', userId, accountId] as const,
  analytics: (userId: string, params: RfqAnalyticsRequest) =>
    [
      ...rfqKeys.all,
      'analytics',
      userId,
      params.accountId,
      params.rfqId ?? 'aggregate',
      params.from ?? '',
      params.to ?? '',
    ] as const,
}

/**
 * RFQ history is an initial dump, not a polling source. Full RFQ snapshots arriving on
 * the account stream are merged over this seed. A fresh consumer mount deliberately
 * reloads the dump; focus and reconnect events do not.
 *
 * Mutations still invalidate the account key explicitly as a recovery path when a
 * command succeeds but its corresponding stream frame is delayed or missed.
 */
export const rfqHistoryQueryOptions = snapshotAndUpdateQueryOptions

/**
 * All three RFQ mutations below invalidate every history query for this user
 * — not the single account the mutation targeted — via the shared `[...all,
 * 'history', userId]` prefix. React Query's `invalidateQueries` fuzzy-matches
 * any query key that has this as a prefix, so it catches both the legacy
 * single-account key (`rfqKeys.history(userId, accountId)`, still used by
 * `useGetRfqHistory`/the standalone RFQ widget) and the batched
 * multi-account key `useRfqLists` now uses (`rfqKeys.history(userId,
 * scopeKey)`) — a precise single-account invalidation would silently miss
 * the batched query entirely, since `scopeKey` isn't `variables.accountId`.
 */
function invalidateRfqHistory(
  queryClient: ReturnType<typeof useQueryClient>,
  userId?: string | null
) {
  queryClient.invalidateQueries({ queryKey: [...rfqKeys.all, 'history', userId ?? ''] })
}

/**
 * Create a manual or auto RFQ. The response handle tells the caller which it is
 * (`rfqId` = manual, `correlationId` = auto) — use `interpretCreateRfqResponse`.
 * For auto RFQs the caller should stamp `payload.correlationId` (e.g.
 * `crypto.randomUUID()`) so a start-failure can be matched, mirroring algo starts.
 */
export const useCreateRfq = (props?: {
  onSuccess?: (data: CreateRfqResponse, variables: CreateRfqRequest) => void
  onError?: (error: ErrorResponse, variables: CreateRfqRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: CreateRfqRequest) => createRfq(payload),
    onSuccess: (data, variables) => {
      invalidateRfqHistory(queryClient, user?.userId)
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useCancelRfq = (props?: {
  onSuccess?: (data: CancelRfqResponse, variables: CancelRfqRequest) => void
  onError?: (error: ErrorResponse, variables: CancelRfqRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const store = useStore()
  return useMutation({
    mutationFn: (payload: CancelRfqRequest) => cancelRfq(payload),
    onSuccess: (data, variables) => {
      if (data.accepted) {
        store.set(rfqsRawAtom, (prev) => markRfqCancelled(prev, variables))
        queryClient.setQueriesData<RfqUpdate[]>(
          { queryKey: [...rfqKeys.all, 'history', user?.userId ?? ''] },
          (prev) => (prev ? markRfqCancelled(prev, variables) : prev)
        )
      }
      invalidateRfqHistory(queryClient, user?.userId)
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useAcceptRfqQuote = (props?: {
  onSuccess?: (data: AcceptRfqQuoteResponse, variables: AcceptRfqQuoteRequest) => void
  onError?: (error: ErrorResponse, variables: AcceptRfqQuoteRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (payload: AcceptRfqQuoteRequest) => acceptRfqQuote(payload),
    onSuccess: (data, variables) => {
      invalidateRfqHistory(queryClient, user?.userId)
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

/**
 * Maker directory for the targeted-maker picker. A live venue request with a 3s
 * timeout, so fetch lazily (pass `enabled` when the picker opens) and cache for the
 * session; a timeout surfaces as an error the UI can show as "makers unavailable".
 */
export const useGetRfqMakers = (accountId: string, options?: { enabled?: boolean }) => {
  const { user } = useAuth()
  return useQuery<string[], ErrorResponse>({
    queryKey: rfqKeys.makers(user?.userId ?? '', accountId),
    queryFn: async () => {
      const res = await getRfqMakers({ accountId })
      return res.makerIds
    },
    enabled: !!user && !!accountId && options?.enabled !== false,
    staleTime: Infinity,
    retry: false,
  })
}

/**
 * RFQ analytics — aggregate stats over a window (`rfqId` unset) or a single-RFQ
 * drill-down (`rfqId` set). A read-only reporting surface; it never drives the live
 * ticket flow, so a short staleTime is fine.
 */
export const useRfqAnalytics = (params: RfqAnalyticsRequest, options?: { enabled?: boolean }) => {
  const { user } = useAuth()
  return useQuery<RfqAnalytics, ErrorResponse>({
    queryKey: rfqKeys.analytics(user?.userId ?? '', params),
    queryFn: () => fetchRfqAnalytics(params),
    enabled: !!user && !!params.accountId && options?.enabled !== false,
    staleTime: 30_000,
  })
}

/** Seed the RFQ list with lifecycle rows (bids/asks are empty here — never accept off one). */
export const useGetRfqHistory = (
  params: { accountId: string; from?: string; to?: string; limit?: string },
  options?: { enabled?: boolean }
) => {
  const { user } = useAuth()
  return useQuery<RfqUpdate[], ErrorResponse>({
    queryKey: rfqKeys.history(user?.userId ?? '', params.accountId),
    queryFn: async () => {
      const res = await getRfqHistory({ ...params, accountIds: [] })
      return res.rfqs
    },
    enabled: !!user && !!params.accountId && options?.enabled !== false,
    ...rfqHistoryQueryOptions,
  })
}

/**
 * Maker quote send / re-price. On success the routed quote is recorded as pending (keyed by
 * its side slot) until the stream answers, which shows "sending…" and makes a retry reuse
 * the same quote_id.
 */
export const useSendRfqMakerQuote = (props?: {
  onSuccess?: (data: SendRfqMakerQuoteResponse, variables: SendRfqMakerQuoteRequest) => void
  onError?: (error: ErrorResponse, variables: SendRfqMakerQuoteRequest) => void
}) => {
  const store = useStore()
  return useMutation({
    mutationFn: (payload: SendRfqMakerQuoteRequest) => sendRfqMakerQuote(payload),
    // The re-priced quote's stamp BEFORE the send: the stream can answer before the HTTP
    // response lands, so reading it in onSuccess could swallow that answer.
    onMutate: (variables) => ({
      baseUpdatedAtMs: variables.quoteId
        ? store.get(makerQuotesAtom)[variables.quoteId]?.updatedAtMs
        : undefined,
    }),
    onSuccess: (data, variables, context) => {
      if (data?.quoteId) {
        const pending: PendingMakerQuote = {
          quoteId: data.quoteId,
          accountId: variables.accountId,
          rfqId: variables.rfqId,
          side: variables.side,
          price: variables.price,
          sentAtMs: Date.now(),
          baseUpdatedAtMs: context?.baseUpdatedAtMs,
        }
        store.set(pendingMakerQuotesAtom, (prev) => ({ ...prev, [quoteSlotKey(pending)]: pending }))
      }
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

/** Maker quote cancel; routed only — the CANCELLED state arrives on the stream. */
export const useCancelRfqMakerQuote = (props?: {
  onSuccess?: (data: CancelRfqMakerQuoteResponse, variables: CancelRfqMakerQuoteRequest) => void
  onError?: (error: ErrorResponse, variables: CancelRfqMakerQuoteRequest) => void
}) =>
  useMutation({
    mutationFn: (payload: CancelRfqMakerQuoteRequest) => cancelRfqMakerQuote(payload),
    onSuccess: props?.onSuccess,
    onError: props?.onError,
  })
